import { FontFamily } from "@redotech/redo-content-builder-common/element-schema";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { pageLayoutSchema } from "../packing-slips/packing-slip-definition";
import { WorkOrderFlowTriggerConditions } from "../work-order-flow/work-order-flow";

/** Maximum number of products to fetch for AI recommendations */
export const MAX_PRODUCTS_FOR_RECOMMENDATIONS = 50;

export enum PersonalizedSlipBlockType {
  DISCOUNT = "Discount",
  IMAGE = "Image",
  LINE = "Line",
  PRODUCT_RECOMMENDATION = "AI product recommendation",
  QR_CODE = "QR Code",
  SHAPE = "Shape",
  SPACER = "Spacer",
  TEXT = "Text",
}

// Shape type identifiers — mostly sourced from `redo/icons/src/<name>_filled.svg`.
// Keep in sync with SHAPE_DEFS in fulfillment/documents/.../blocks/shape-paths.ts.
export const slipShapeType = [
  // Basic shapes (incl. polygons)
  "square",
  "rounded-square",
  "circle",
  "triangle",
  "pentagon",
  "hexagon-1",
  "hexagon-2",
  "octagon",
  // Stars
  "star-01",
  "star-02",
  "star-03",
  "star-04",
  "star-06",
  "star-07",
  "asterisk-1",
  "stars-1",
  "stars-2",
  "stars-3",
  // Arrows — chunky block + skinny narrow
  "arrow-up",
  "arrow-down",
  "arrow-left",
  "arrow-right",
  "arrow-narrow-up",
  "arrow-narrow-down",
  "arrow-narrow-left",
  "arrow-narrow-right",
  // Hearts
  "heart",
  "hearts",
  "heart-rounded",
  "heart-square",
  "heart-circle",
  "heart-hand",
  // Banners
  "flag-01",
  "flag-03",
  "flag-04",
  "bookmark",
  // Celestial
  "cloud",
  "sun",
  "sunrise",
  "sunset",
  "moon-1",
  "moon-2",
  "lightning-1",
  "lightning-2",
  "magic-wand-1",
  "magic-wand-2",
  // Awards
  "award-01",
  "award-02",
  "award-03",
  "award-04",
  "award-05",
  "certificate-1",
  "certificate-2",
] as const;
export type SlipShapeType = (typeof slipShapeType)[number];

export const slipLineStyle = ["solid", "dashed", "dotted"] as const;
export type SlipLineStyle = (typeof slipLineStyle)[number];

export const slipBlockSide = ["front", "back"] as const;
export type SlipBlockSide = (typeof slipBlockSide)[number];

const personalizedSlipSettingsSchema_ = z
  .object({
    pageLayout: pageLayoutSchema.meta({ privacy: privacy.structured }),
    backgroundColor: z
      .string()
      .meta({ privacy: privacy.enumOrStatus })
      .nullish(),
    backgroundImageUrl: z
      .string()
      .meta({ privacy: privacy.publicUrl })
      .nullish(),
    backgroundImageOpacity: z
      .number()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    twoSided: z.boolean().meta({ privacy: privacy.flag }).nullish(),
    backBackgroundColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    backBackgroundImageUrl: z
      .string()
      .meta({ privacy: privacy.publicUrl })
      .nullish(),
    backBackgroundImageOpacity: z
      .number()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    fontFamily: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    fontColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    fontSize: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  })
  .meta({ privacy: privacy.structured });

export const personalizedSlipSettingsSchema: z.ZodType<PersonalizedSlipSettings> =
  personalizedSlipSettingsSchema_;
export interface PersonalizedSlipSettings extends z.infer<
  typeof personalizedSlipSettingsSchema_
> {}

const baseSectionSchema_ = z
  .object({
    id: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    type: createEnum(PersonalizedSlipBlockType, {
      enumName: "PersonalizedSlipBlockType",
    }).meta({ privacy: privacy.nonPii }),
    x: z.number().meta({ privacy: privacy.nonPii }),
    y: z.number().meta({ privacy: privacy.nonPii }),
    zIndex: z.number().meta({ privacy: privacy.nonPii }),
    width: z.number().meta({ privacy: privacy.nonPii }),
    height: z.number().meta({ privacy: privacy.nonPii }),
    rotation: z.number().meta({ privacy: privacy.nonPii }),
    side: createEnum(slipBlockSide, { enumName: "SlipBlockSide" })
      .default("front")
      .meta({ privacy: privacy.nonPii }),
    backgroundColor: z
      .string()
      .meta({ privacy: privacy.enumOrStatus })
      .nullish(),
    borderColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    borderWidth: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    borderRadius: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  })
  .meta({ privacy: privacy.structured });

export const baseSectionSchema: z.ZodType<BaseBlock> = baseSectionSchema_;
export interface BaseBlock extends z.infer<typeof baseSectionSchema_> {}

export const textAlignment = ["left", "center", "right"] as const;
export type TextAlignment = (typeof textAlignment)[number];

export const textBlockType = ["static", "title", "subtitle", "custom"] as const;
export type TextBlockType = (typeof textBlockType)[number];

/** Text block types that are AI-generated (everything except "static") */
export const aiGeneratedTextTypes: readonly TextBlockType[] = [
  "title",
  "subtitle",
  "custom",
];

/** Check if a text type requires AI generation */
export function isAiGeneratedTextType(
  textType: TextBlockType | null | undefined,
): boolean {
  return textType != null && aiGeneratedTextTypes.includes(textType);
}

export const fontWeight = [
  "normal",
  "bold",
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
  "800",
  "900",
] as const;
export type FontWeight = (typeof fontWeight)[number];

const textSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.TEXT)
    .meta({ privacy: privacy.nonPii }),
  text: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  linkColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  fontSize: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  fontFamily: createEnum(FontFamily, { enumName: "FontFamily" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  fontColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  // Typography enhancements
  fontWeight: createEnum(fontWeight, { enumName: "SlipFontWeight" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  textAlignment: createEnum(textAlignment, { enumName: "SlipTextAlignment" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  lineHeight: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  letterSpacing: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  // Background image
  backgroundImage: z.string().meta({ privacy: privacy.publicUrl }).nullish(),
  backgroundOverlayColor: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  backgroundOverlayOpacity: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .min(0)
    .max(100)
    .nullish(),
  // AI text generation
  textType: createEnum(textBlockType, { enumName: "SlipTextBlockType" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  aiPrompt: z.string().meta({ privacy: privacy.modelPrompt }).nullish(), // Only used when textType === 'custom'
  /** Fits text to its box (grow then shrink) before clipping; false keeps a fixed box and clips overflow. Defaults on. */
  autoFitText: z.boolean().meta({ privacy: privacy.flag }).default(true),
});

export const textSectionSchema: z.ZodType<TextBlock> = textSectionSchema_;
export const textSectionObjectSchema = textSectionSchema_;
export interface TextBlock extends z.infer<typeof textSectionSchema_> {}

const qrCodeSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.QR_CODE)
    .meta({ privacy: privacy.nonPii }),
  qrCode: z.string().meta({ privacy: privacy.publicUrl }),
});
export const qrCodeSectionSchema: z.ZodType<QrCodeBlock> = qrCodeSectionSchema_;
export interface QrCodeBlock extends z.infer<typeof qrCodeSectionSchema_> {}

export const imageObjectFit = ["fill", "fit", "cover"] as const;
export type ImageObjectFit = (typeof imageObjectFit)[number];

const imageSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.IMAGE)
    .meta({ privacy: privacy.nonPii }),
  imageUrl: z.string().meta({ privacy: privacy.publicUrl }).nullish(),
  resolution: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  objectFit: createEnum(imageObjectFit, { enumName: "SlipImageObjectFit" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  overlayColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  overlayOpacity: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .min(0)
    .max(100)
    .nullish(),
});

export const imageSectionSchema: z.ZodType<ImageBlock> = imageSectionSchema_;
export interface ImageBlock extends z.infer<typeof imageSectionSchema_> {}

export const productRecommendationLayout = [
  "adjacent",
  "overlay",
  "image-only",
] as const;
export type ProductRecommendationLayout =
  (typeof productRecommendationLayout)[number];

// This createEnum literal is the source of truth that generates the
// slip_product_selection_mode CongoDB enum — it can't import the codegen it
// produces (circular), and Zod's z.enum(nativeEnum) would force every consumer
// onto the nominal enum type. Matches the other slip enums in this file.
//redontIgnore
export const productSelectionMode = [
  "ai",
  "static",
  "recently-ordered",
  "warm",
] as const;
export type ProductSelectionMode = (typeof productSelectionMode)[number];

// Text placement relative to the product image (no corners)
export const textPlacement = [
  "top", // Text above image
  "left", // Text to the left of image
  "right", // Text to the right of image
  "bottom", // Text below image
] as const;
export type TextPlacement = (typeof textPlacement)[number];

export const productAspectRatio = ["3:2", "1:1", "4:5", "2:3"] as const;
export type ProductAspectRatio = (typeof productAspectRatio)[number];

// Vertical alignment within the text area
export const verticalAlignment = ["top", "center", "bottom"] as const;
export type VerticalAlignment = (typeof verticalAlignment)[number];

const productRecommendationSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.PRODUCT_RECOMMENDATION)
    .meta({ privacy: privacy.nonPii }),

  // Product selection
  productSelectionMode: createEnum(productSelectionMode, {
    enumName: "SlipProductSelectionMode",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }), // Default: "ai"
  staticProductId: z
    .string()
    .meta({ privacy: privacy.id.external.resource })
    .nullish(), // Used when mode is "static"
  staticProductTitle: z
    .string()
    .meta({ privacy: privacy.productCatalogText })
    .nullish(), // Cached for display
  staticVariantTitle: z
    .string()
    .meta({ privacy: privacy.productCatalogText })
    .nullish(), // Cached variant title for display (null/"Default Title" variants are omitted)
  staticProductImageUrl: z
    .string()
    .meta({ privacy: privacy.publicUrl })
    .nullish(), // Cached for display
  staticProductPrice: z.string().meta({ privacy: privacy.money }).nullish(), // Cached for display
  numberOfProducts: z.number().meta({ privacy: privacy.quantity }).nullish(), // Number of products to show (default: 1)
  /** Collection title; product membership is recorded by title, not id. */
  aiProductCollection: z
    .string()
    .meta({ privacy: privacy.productCatalogText })
    .nullish(),
  /** Re-resolved to a title at generation time, so renames don't unscope. */
  aiProductCollectionId: z
    .string()
    .meta({ privacy: privacy.id.external.resource })
    .nullish(),

  // Title display preference
  useVariantTitle: z.boolean().meta({ privacy: privacy.flag }).nullish(), // When true, display the variant title instead of the product title

  // Layout & positioning
  layout: createEnum(productRecommendationLayout, {
    enumName: "SlipProductRecommendationLayout",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  textPlacement: createEnum(textPlacement, { enumName: "SlipTextPlacement" })
    .nullish()
    .meta({ privacy: privacy.nonPii }), // Default: "right"
  verticalAlignment: createEnum(verticalAlignment, {
    enumName: "SlipVerticalAlignment",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }), // Default: "center"
  aspectRatio: createEnum(productAspectRatio, {
    enumName: "SlipProductAspectRatio",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }), // Default: "1:1"

  // Display toggles
  showProductTitle: z.boolean().meta({ privacy: privacy.flag }).nullish(),
  hideProductTitle: z.boolean().meta({ privacy: privacy.flag }).nullish(),
  showProductPrice: z.boolean().meta({ privacy: privacy.flag }).nullish(), // Deprecated - use hideProductPrice instead
  hideProductPrice: z.boolean().meta({ privacy: privacy.flag }).nullish(),
  showDiscountCode: z.boolean().meta({ privacy: privacy.flag }).nullish(),

  // Image styling
  imageRadius: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  imageBorderColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  imageBorderWidth: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  imageObjectFit: createEnum(imageObjectFit, { enumName: "SlipImageObjectFit" })
    .nullish()
    .meta({ privacy: privacy.nonPii }), // Image constraints: fill, fit, cover
  marginHorizontal: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  marginVertical: z.number().meta({ privacy: privacy.nonPii }).nullish(),

  // Text styling
  fontFamily: createEnum(FontFamily, { enumName: "FontFamily" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  fontSize: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  textColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  titleFontSize: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  titleFontColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  titleFontWeight: createEnum(fontWeight, { enumName: "SlipFontWeight" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  textAlignment: createEnum(textAlignment, { enumName: "SlipTextAlignment" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  lineHeight: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  letterSpacing: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  priceFontSize: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  priceFontColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  discountFontSize: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  discountFontColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),

  // Generated content (filled at generation time)
  generatedProductId: z
    .string()
    .meta({ privacy: privacy.id.external.resource })
    .nullish(),
  generatedProductTitle: z
    .string()
    .meta({ privacy: privacy.productCatalogText })
    .nullish(),
  generatedProductDescription: z
    .string()
    .meta({ privacy: privacy.text.redoForMerchant })
    .nullish(),
  generatedProductPrice: z.string().meta({ privacy: privacy.money }).nullish(),
  generatedProductImageUrl: z
    .string()
    .meta({ privacy: privacy.publicUrl })
    .nullish(),
  generatedDiscountCode: z
    .string()
    .meta({ privacy: privacy.id.capability })
    .nullish(),
});

export const productRecommendationSectionSchema: z.ZodType<ProductRecommendationBlock> =
  productRecommendationSectionSchema_;
export interface ProductRecommendationBlock extends z.infer<
  typeof productRecommendationSectionSchema_
> {}

// LINE block - simple divider line, optionally styled and with arrow caps.
const lineSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.LINE)
    .meta({ privacy: privacy.nonPii }),
  lineColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  lineThickness: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  lineStyle: createEnum(slipLineStyle, { enumName: "SlipLineStyle" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  arrowStart: z.boolean().meta({ privacy: privacy.flag }).nullish(),
  arrowEnd: z.boolean().meta({ privacy: privacy.flag }).nullish(),
});

export const lineSectionSchema: z.ZodType<LineBlock> = lineSectionSchema_;
export interface LineBlock extends z.infer<typeof lineSectionSchema_> {}

// SHAPE block - geometric or decorative shape rendered as an SVG path.
const shapeSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.SHAPE)
    .meta({ privacy: privacy.nonPii }),
  shapeType: createEnum(slipShapeType, { enumName: "SlipShapeType" }).meta({
    privacy: privacy.nonPii,
  }),
  fillColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  opacity: z
    .number()
    .meta({ privacy: privacy.nonPii })
    .min(0)
    .max(100)
    .nullish(),
});

export const shapeSectionSchema: z.ZodType<ShapeBlock> = shapeSectionSchema_;
export interface ShapeBlock extends z.infer<typeof shapeSectionSchema_> {}

// SPACER block - empty space for layout
const spacerSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.SPACER)
    .meta({ privacy: privacy.nonPii }),
});
export const spacerSectionSchema: z.ZodType<SpacerBlock> = spacerSectionSchema_;
export interface SpacerBlock extends z.infer<typeof spacerSectionSchema_> {}

// Offer text type for discount block
export const offerTextType = ["ai", "static", "none"] as const;
export type OfferTextType = (typeof offerTextType)[number];

export const qrCodePosition = ["left", "right"] as const;
export type QrCodePosition = (typeof qrCodePosition)[number];

// DISCOUNT block - displays discount with optional QR code
const discountSectionSchema_ = baseSectionSchema_.extend({
  type: z
    .literal(PersonalizedSlipBlockType.DISCOUNT)
    .meta({ privacy: privacy.nonPii }),
  // Discount selection
  discountId: zExt.objectId().nullish().meta({ privacy: privacy.id.internal }),
  generateUniquePerCustomer: z
    .boolean()
    .meta({ privacy: privacy.flag })
    .nullish(),
  // Offer text
  offerTextType: createEnum(offerTextType, { enumName: "SlipOfferTextType" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  offerText: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
  // QR code
  includeQrCode: z.boolean().meta({ privacy: privacy.flag }).nullish(),
  qrCodeUrl: z.string().meta({ privacy: privacy.publicUrl }).nullish(),
  qrCodePosition: createEnum(qrCodePosition, { enumName: "SlipQrCodePosition" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  // Styling
  fontFamily: createEnum(FontFamily, { enumName: "FontFamily" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  fontSize: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  fontWeight: createEnum(fontWeight, { enumName: "SlipFontWeight" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  textAlignment: createEnum(textAlignment, { enumName: "SlipTextAlignment" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  textColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  blockBackgroundColor: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  // Generated content (filled at generation time)
  generatedDiscountCode: z
    .string()
    .meta({ privacy: privacy.id.capability })
    .nullish(),
  generatedOfferText: z
    .string()
    .meta({ privacy: privacy.text.redoForMerchant })
    .nullish(),
});

export const discountSectionSchema: z.ZodType<DiscountBlock> =
  discountSectionSchema_;
export interface DiscountBlock extends z.infer<typeof discountSectionSchema_> {}

const personalizedSlipBlockSchema_ = z.discriminatedUnion("type", [
  textSectionSchema_,
  qrCodeSectionSchema_,
  imageSectionSchema_,
  productRecommendationSectionSchema_,
  lineSectionSchema_,
  spacerSectionSchema_,
  discountSectionSchema_,
  shapeSectionSchema_,
]);
export const personalizedSlipBlockSchema: z.ZodType<PersonalizedSlipBlock> =
  personalizedSlipBlockSchema_;

export type PersonalizedSlipBlock = z.infer<
  typeof personalizedSlipBlockSchema_
>;

const personalizedSlipSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.id.internal }),
    team: zExt.objectId().meta({ privacy: privacy.id.internal }),
    createdAt: z.string().meta({ privacy: privacy.timestamp }),
    updatedAt: z.string().meta({ privacy: privacy.timestamp }),
    name: z.string().meta({ privacy: privacy.resourceLabel }),
    blocks: z
      .array(personalizedSlipBlockSchema)
      .meta({ privacy: privacy.structured }),
    globalSettings: personalizedSlipSettingsSchema.meta({
      privacy: privacy.structured,
    }),
    /** Render one page per eligible line item instead of one insert per shipment. */
    perLineItem: z.boolean().meta({ privacy: privacy.flag }).nullish(),
    /** Line items that get a page when `perLineItem`; unset means every item. */
    lineItemFilter: WorkOrderFlowTriggerConditions.nullish().meta({
      privacy: privacy.untypedPayload,
    }),
  })
  .meta({ privacy: privacy.structured });
export const personalizedSlipSchema: z.ZodType<PersonalizedSlip> =
  personalizedSlipSchema_;
export interface PersonalizedSlip extends z.infer<
  typeof personalizedSlipSchema_
> {}

const personalizedSlipVariablesSchema = z
  .object({
    customerFirstName: z
      .string()
      .meta({ privacy: privacy.person_name.customer }),
    customerLastName: z
      .string()
      .meta({ privacy: privacy.person_name.customer }),
    customerFullName: z
      .string()
      .meta({ privacy: privacy.person_name.customer }),
    customerOrderCount: z
      .string()
      .meta({ privacy: privacy.quantity })
      .nullish(),
    orderNotes: z.string().meta({ privacy: privacy.text.customer }),
    orderedProductName: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    orderedProductFamilyName: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    orderedProductCategory: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    recommendedProductName: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    discountUrl: z.string().meta({ privacy: privacy.publicUrl }).nullish(),
    discountCode: z.string().meta({ privacy: privacy.id.capability }).nullish(),
    // Set only on the pages of a per-line-item insert.
    lineItemTitle: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    lineItemProductTitle: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    lineItemVariantTitle: z
      .string()
      .meta({ privacy: privacy.productCatalogText })
      .nullish(),
    lineItemSku: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    lineItemQuantity: z.string().meta({ privacy: privacy.quantity }).nullish(),
    lineItemAttributes: z
      .array(
        z
          .object({
            key: z.string().meta({ privacy: privacy.nonPii }),
            value: z.string().meta({ privacy: privacy.text.customer }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured })
      .nullish(),
  })
  .meta({ privacy: privacy.structured });

export type PersonalizedSlipVariables = z.infer<
  typeof personalizedSlipVariablesSchema
>;

export const RICH_TEXT_VARIABLES: (keyof PersonalizedSlipVariables)[] = [
  "customerFirstName",
  "customerLastName",
  "customerFullName",
  "customerOrderCount",
  "orderNotes",
  "orderedProductName",
  "orderedProductFamilyName",
  "orderedProductCategory",
  "recommendedProductName",
  "discountUrl",
  "discountCode",
];

/** Offered only on per-line-item templates, where each page has one item. */
export const LINE_ITEM_RICH_TEXT_VARIABLES: (keyof PersonalizedSlipVariables)[] =
  [
    "lineItemTitle",
    "lineItemProductTitle",
    "lineItemVariantTitle",
    "lineItemSku",
    "lineItemQuantity",
  ];

/** `{{ line_item.attributes.gift_message }}` reads the `gift_message` attribute. */
export const LINE_ITEM_ATTRIBUTE_VARIABLE_PREFIX = "line_item.attributes.";

/** A collection an AI product block is scoped to; membership resolves by id. */
export interface SlipRecommendationCollection {
  id?: string | null;
  title: string;
}

export type SlipRecommendationSource = "ai" | "fallback" | "none";

/**
 * Product ids in and out are Shopify product GIDs. Injected so the slip step
 * stays decoupled from the recommendations engine.
 */
export type GenerateProductRecommendationsFunc = (params: {
  teamId: string;
  productIds: string[];
  customerEmail?: string;
  limit?: number;
  collection?: SlipRecommendationCollection;
}) => Promise<{
  productIds: string[];
  recommendationSource: SlipRecommendationSource;
}>;
